<template>
  <div>
    <div class="filter-bar">
      <el-input
        v-model="search"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="Search identifier, friendly name, location or model"
        style="width:280px"
      />
      <el-select
        v-model="status"
        size="small"
        clearable
        placeholder="All Statuses"
        style="width:160px"
        @change="loadDisplays"
        ><el-option
          v-for="item in statuses"
          :key="item"
          :label="item"
          :value="item"
      /></el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadDisplays"
        >Refresh</el-button
      >
      <span class="result-count"
        >{{ filteredDisplays.length }} Dashboard Displays</span
      >
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreate"
        >Add Dashboard Display</el-button
      >
    </div>
    <el-table
      :data="filteredDisplays"
      v-loading="loading"
      border
      class="app-table"
      :max-height="tableHeight"
    >
      <el-table-column
        prop="deviceIdentifier"
        label="Device Identifier"
        min-width="210"
      />
      <el-table-column
        prop="identifierType"
        label="Identifier Type"
        width="150"
      />
      <el-table-column prop="locationName" label="Location" min-width="190" />
      <el-table-column
        prop="friendlyName"
        label="Friendly Name"
        min-width="180"
        show-overflow-tooltip
      >
        <template slot-scope="{ row }">{{ row.friendlyName || '--' }}</template>
      </el-table-column>
      <el-table-column prop="model" label="Model" min-width="170" />
      <el-table-column prop="status" label="Status" width="140"
        ><template slot-scope="{ row }"
          ><el-tag :type="statusType(row.status)" size="small">{{
            row.status
          }}</el-tag></template
        ></el-table-column
      >
      <el-table-column label="Actions" width="110" align="center" fixed="right"
        ><template slot-scope="{ row }"
          ><el-button
            v-if="$permission('Installation Tool.Devices.Edit')"
            type="danger"
            plain
            size="small"
            :disabled="row.status && row.status !== 'Unassigned'"
            @click="deleteDisplay(row)"
            >Delete</el-button
          ></template
        ></el-table-column
      >
    </el-table>
    <el-dialog
      title="Add Dashboard Display"
      :visible.sync="showCreate"
      width="520px"
      @close="resetForm"
    >
      <el-form
        :model="form"
        :rules="rules"
        ref="displayForm"
        label-width="150px"
      >
        <el-form-item label="Device Identifier" prop="deviceIdentifier"
          ><el-input v-model="form.deviceIdentifier" placeholder="Serial number"
        /></el-form-item>
        <el-form-item label="Identifier Type" prop="identifierType"
          ><el-select v-model="form.identifierType" style="width:100%"
            ><el-option label="Serial Number" value="SerialNumber"/></el-select
        ></el-form-item>
        <el-form-item label="Location Name" prop="locationName"
          ><el-input v-model="form.locationName" placeholder="Location name"
        /></el-form-item>
        <el-form-item label="Model" prop="model"
          ><el-input v-model="form.model" placeholder="Optional"
        /></el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer"
        ><el-button class="save-btn" :loading="saving" @click="createDisplay"
          >ADD TO POOL</el-button
        ><el-button class="cancel-btn" @click="showCreate = false"
          >CANCEL</el-button
        ></span
      >
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import loraTableHeight from '@/mixins/loraTableHeight'

const emptyForm = () => ({
  deviceIdentifier: '',
  identifierType: 'SerialNumber',
  locationName: '',
  model: ''
})
export default {
  name: 'LoraDashboardDisplayPool',
  mixins: [loraTableHeight],
  data() {
    return {
      displays: [],
      loading: false,
      saving: false,
      search: '',
      status: '',
      statuses: ['Unassigned', 'Assigned', 'Commissioned'],
      showCreate: false,
      form: emptyForm(),
      rules: {
        deviceIdentifier: [
          { required: true, message: 'Required', trigger: 'blur' }
        ],
        identifierType: [
          { required: true, message: 'Required', trigger: 'change' }
        ],
        locationName: [{ required: true, message: 'Required', trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredDisplays() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.displays
      return this.displays.filter(display =>
        [
          display.deviceIdentifier,
          display.locationName,
          display.friendlyName,
          display.model,
          display.identifierType,
          display.status
        ].some(value =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      )
    }
  },
  mounted() {
    this.loadDisplays()
  },
  methods: {
    responseItems(response) {
      const body = response.data || []
      const items = body.data || body.items || body.Items || body
      return Array.isArray(items) ? items : []
    },
    async loadDisplays() {
      this.loading = true
      try {
        const response = await LoraApiService.getDashboardDisplays(
          this.status ? { status: this.status } : {}
        )
        this.displays = this.responseItems(response)
      } catch (error) {
        this.$message.error(
          loraError(error, 'Failed to load Dashboard Display pool')
        )
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.showCreate = true
    },
    resetForm() {
      this.$refs.displayForm && this.$refs.displayForm.resetFields()
    },
    async createDisplay() {
      const valid = await this.$refs.displayForm.validate().catch(() => false)
      if (!valid) return
      this.saving = true
      try {
        await LoraApiService.createDashboardDisplay({
          deviceIdentifier: this.form.deviceIdentifier.trim(),
          identifierType: this.form.identifierType,
          locationName: this.form.locationName.trim(),
          model: this.form.model || undefined
        })
        this.$message.success('Dashboard Display added to pool')
        this.showCreate = false
        this.loadDisplays()
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to add Dashboard Display'))
      } finally {
        this.saving = false
      }
    },
    async deleteDisplay(display) {
      try {
        await this.$confirm(
          'Delete Dashboard Display ' +
            display.deviceIdentifier +
            ' from the pool?',
          'Confirm Delete',
          { type: 'warning', confirmButtonText: 'Delete' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.deleteDashboardDisplay(display.id)
        this.$message.success('Dashboard Display deleted')
        this.loadDisplays()
      } catch (error) {
        this.$message.error(
          loraError(error, 'Failed to delete Dashboard Display')
        )
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : '--'
    },
    statusType(status) {
      return (
        { Unassigned: 'info', Assigned: 'warning', Commissioned: 'success' }[
          status
        ] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  .result-count {
    margin-left: auto;
    color: $font-color;
    font-size: 13px;
  }
}
</style>
